import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  CheckIcon,
  ClipboardDocumentIcon,
  Bars3Icon,
  ClockIcon,
} from "@heroicons/react/24/outline";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import EpisodeCard from "@/components/EpisodeCard";
import EpisodeModal from "@/components/EpisodeModal";
import SessionSidebar from "@/components/chat/SessionSidebar";
import MobileSessionDrawer from "@/components/chat/MobileSessionDrawer";
import NewSessionModal from "@/components/chat/NewSessionModal";
import DeleteSessionWarningModal from "@/components/chat/DeleteSessionWarningModal";
import AnswerEvidencePanel from "@/components/chat/AnswerEvidencePanel";
import { usePodcast } from "@/contexts/PodcastContext";
import {
  apiClient,
  SessionExpiredError,
  type AskSession,
  type AskSessionMessage,
  type AskResponseMetadata,
} from "@/api/client";
import { useEpisodes, type Episode } from "@/hooks/useEpisodes";
import { useCredits } from "@/hooks/useCredits";
import { useGlobalAskSessions } from "@/hooks/useGlobalAskSessions";
import { useSessionRequestState } from "@/hooks/useSessionRequestState";
import { loadSessionRequestState } from "@/lib/session-storage";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";
import { isPodcastProcessing } from "@/lib/podcast-utils";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  responseId?: string;
  verdict?: "answer" | "insufficient" | "no_context";
  usedContext?: "current" | "previous" | "both" | "reset";
  missing?: string[];
  chunksUsed?: number;
  model?: string;
  referencedEpisodeNumbers?: number[];
  metadata?: AskResponseMetadata | null;
}

const formatUsedContext = (value?: ChatMessage["usedContext"]) => {
  switch (value) {
    case "current":
      return "Current context";
    case "previous":
      return "Previous reply context";
    case "both":
      return "Blended context";
    case "reset":
      return "Context reset";
    default:
      return undefined;
  }
};

const formatMessageForCopy = (message: ChatMessage) => {
  const sections: string[] = [];
  const body = (message.content ?? "").trimEnd();

  if (body) {
    sections.push(body);
  }

  const meta: string[] = [];

  if (message.role === "assistant" && message.verdict) {
    const verdictLabel =
      message.verdict === "answer"
        ? "Answer provided"
        : message.verdict === "no_context"
          ? "No context available"
          : "Insufficient context";
    meta.push(`Verdict: ${verdictLabel}`);

    const used = formatUsedContext(message.usedContext);
    if (used) {
      meta.push(`Context: ${used}`);
    }

    if (typeof message.chunksUsed === "number") {
      meta.push(`Chunks used: ${message.chunksUsed}`);
    }

    if (message.model) {
      meta.push(`Model: ${message.model}`);
    }

    if (message.metadata) {
      const referenced = message.metadata.referenced_episodes
        .map((episode) =>
          episode.title
            ? `#${episode.episode_number} ${episode.title}`
            : `#${episode.episode_number}`,
        )
        .join(", ");
      if (referenced) {
        meta.push(`Referenced episodes: ${referenced}`);
      }

      if (message.metadata.sources.length) {
        meta.push(`Sources: ${message.metadata.sources.length}`);
      }
    }
  }

  if (message.missing?.length) {
    meta.push(
      `Missing information:\n${message.missing.map((item) => `- ${item}`).join("\n")}`,
    );
  }

  if (meta.length) {
    sections.push(meta.join("\n"));
  }

  return sections.join("\n\n---\n\n") || body;
};

function convertApiMessages(apiMessages: AskSessionMessage[]): ChatMessage[] {
  return apiMessages.map((msg, index) => ({
    id: msg.id != null ? String(msg.id) : `api-msg-${index}`,
    role: msg.role,
    content: msg.content,
    timestamp: new Date(msg.created_at).getTime(),
    responseId: msg.response_id ?? undefined,
    referencedEpisodeNumbers: msg.referenced_episode_numbers ?? undefined,
    metadata: msg.metadata ?? null,
  }));
}

function extractReferencedEpisodeNumbers(
  referencedEpisodeNumbers?: number[] | null,
  metadata?: AskResponseMetadata | null,
): number[] {
  const candidates = metadata?.referenced_episodes.length
    ? metadata.referenced_episodes.map((episode) => episode.episode_number)
    : (referencedEpisodeNumbers ?? []);

  return Array.from(
    new Set(
      candidates.filter(
        (value): value is number =>
          typeof value === "number" && Number.isFinite(value),
      ),
    ),
  );
}

function formatTimeRemaining(expiresAt: string): string {
  const now = Date.now();
  const expiry = new Date(expiresAt).getTime();
  const remaining = expiry - now;

  if (remaining <= 0) return "Expired";

  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m left`;
  }
  return `${minutes}m left`;
}

function isExpiringSoon(expiresAt: string): boolean {
  const now = Date.now();
  const expiry = new Date(expiresAt).getTime();
  const remaining = expiry - now;
  return remaining > 0 && remaining < 30 * 60 * 1000;
}

export default function ChatPage() {
  const {
    selectedPodcast,
    setSelectedPodcast,
    availablePodcasts,
    isLoading: podcastLoading,
  } = usePodcast();
  const { refreshCredits } = useCredits();
  const {
    sessions: globalSessions,
    isLoading: sessionsLoading,
    oldestSession,
    canCreateNew,
    refresh: refreshSessions,
  } = useGlobalAskSessions();

  // Session state - must be declared before displayedPodcast
  const [activeSession, setActiveSession] = useState<AskSession | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [processingPodcastIds, setProcessingPodcastIds] = useState<Set<string>>(new Set());

  // Derive displayed podcast from active session to avoid race condition with context updates
  const displayedPodcast = activeSession
    ? availablePodcasts.find((p) => p.id === activeSession.podcast_slug) ?? selectedPodcast
    : selectedPodcast;

  // Fetch episodes for the displayed podcast (not selectedPodcast to handle cross-podcast sessions)
  const { episodes, isLoading: episodesLoading } = useEpisodes(
    displayedPodcast?.id,
  );

  const [isNewSessionModalOpen, setIsNewSessionModalOpen] = useState(false);
  const [isDeleteWarningOpen, setIsDeleteWarningOpen] = useState(false);
  const [pendingNewSessionSlug, setPendingNewSessionSlug] = useState<
    string | null
  >(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [error, setError] = useState("");
  const [copiedChat, setCopiedChat] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [episodeReferenceCounts, setEpisodeReferenceCounts] = useState<
    Record<number, number>
  >({});
  const [selectedEpisode, setSelectedEpisode] = useState<Episode | null>(null);
  const [enableThinking, setEnableThinking] = useState(true);

  // Per-session request state with localStorage persistence
  const {
    state: requestState,
    isThinking,
    setThinking,
    setError: setRequestError,
    setIdle,
    clearForSession,
  } = useSessionRequestState(activeSession?.session_id ?? null);

  // Derive isLoading from the state machine
  const isLoading = isThinking;

  // Check if current podcast is still being indexed
  const isCurrentPodcastProcessing = Boolean(
    displayedPodcast && processingPodcastIds.has(displayedPodcast.id),
  );

  // Track if we were recently backgrounded (for error recovery)
  const wasBackgroundedRef = useRef(false);

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Timer state for active session
  const [timeRemaining, setTimeRemaining] = useState<string>("");
  const [expiringSoon, setExpiringSoon] = useState(false);

  // Update timer for active session
  useEffect(() => {
    if (!activeSession) {
      setTimeRemaining("");
      setExpiringSoon(false);
      return;
    }

    const updateTime = () => {
      setTimeRemaining(formatTimeRemaining(activeSession.expires_at));
      setExpiringSoon(isExpiringSoon(activeSession.expires_at));
    };

    updateTime();
    const interval = setInterval(updateTime, expiringSoon ? 1000 : 60000);
    return () => clearInterval(interval);
  }, [activeSession, expiringSoon]);

  // Track which podcasts are still processing (not ready for chat)
  useEffect(() => {
    if (!displayedPodcast || episodesLoading) return;

    const isProcessing = isPodcastProcessing(displayedPodcast, episodes);

    setProcessingPodcastIds((prev) => {
      const next = new Set(prev);
      if (isProcessing) {
        next.add(displayedPodcast.id);
      } else {
        next.delete(displayedPodcast.id);
      }
      // Only return new set if actually changed
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) {
        return prev;
      }
      return next;
    });
  }, [displayedPodcast, episodes, episodesLoading]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current && chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Track backgrounding for error recovery
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        wasBackgroundedRef.current = true;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Connection recovery: refresh session when app becomes visible (mobile resume)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      // Only act when becoming visible, with an active session that has a pending request
      if (
        document.visibilityState !== "visible" ||
        !activeSession ||
        requestState.status !== "thinking"
      ) {
        return;
      }

      // Attempt recovery from server
      try {
        const recovered = await tryRecoverFromServer(
          activeSession.podcast_slug,
          activeSession.session_id,
          requestState.messageCount,
        );

        if (recovered) {
          // Server has the answer - update state
          setMessages(convertApiMessages(recovered.messages));

          // Rebuild episode reference counts
          const counts: Record<number, number> = {};
          recovered.messages.forEach((msg) => {
            if (msg.referenced_episode_numbers) {
              msg.referenced_episode_numbers.forEach((num) => {
                counts[num] = (counts[num] ?? 0) + 1;
              });
            }
          });
          setEpisodeReferenceCounts(counts);

          // Clear pending state - response was recovered
          setIdle();
          setError("");
          wasBackgroundedRef.current = false;

          // Refresh sessions list and credits
          refreshSessions();
          void refreshCredits();
        }
        // If not recovered, leave in thinking state - the original request may still complete
      } catch (err) {
        // Handle session expired or deleted
        if (err instanceof SessionExpiredError) {
          setIdle();
          setSessionExpired(true);
          setError("This session has expired. Please start a new session.");
        }
        // For other errors, leave state as-is - the original request may still complete
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [activeSession, requestState, setIdle, refreshSessions, refreshCredits]);

  // Track if we've started recovery polling (to prevent duplicate polling)
  const recoveryStartedRef = useRef(false);

  // Mount recovery: Poll for response if we loaded into "thinking" state (e.g., after page refresh)
  useEffect(() => {
    // Only run if status is "thinking" with an active session and we haven't already started
    if (requestState.status !== "thinking" || !activeSession || recoveryStartedRef.current) return;

    // Mark that we've started recovery to prevent duplicate polling
    recoveryStartedRef.current = true;

    let cancelled = false;
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const attemptRecovery = async (): Promise<boolean> => {
      if (cancelled || !activeSession) return false;

      try {
        const session = await apiClient.getAskSession(
          activeSession.podcast_slug,
          activeSession.session_id,
        );

        if (session.messages.length > requestState.messageCount) {
          // Recovered! Server has the response
          setMessages(convertApiMessages(session.messages));

          // Rebuild episode reference counts
          const counts: Record<number, number> = {};
          session.messages.forEach((msg) => {
            if (msg.referenced_episode_numbers) {
              msg.referenced_episode_numbers.forEach((num) => {
                counts[num] = (counts[num] ?? 0) + 1;
              });
            }
          });
          setEpisodeReferenceCounts(counts);

          setIdle();
          refreshSessions();
          void refreshCredits();
          return true;
        }
      } catch (err) {
        if (err instanceof SessionExpiredError) {
          setIdle();
          setSessionExpired(true);
          setError("This session has expired. Please start a new session.");
          return true; // Stop polling
        }
        console.error("Mount recovery poll failed", err);
      }
      return false;
    };

    // Immediate first attempt
    void attemptRecovery().then((recovered) => {
      if (recovered || cancelled) return;

      // Set up polling every 3 seconds
      pollInterval = setInterval(() => {
        void attemptRecovery().then((recovered) => {
          if (recovered && pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
            if (timeoutId) {
              clearTimeout(timeoutId);
              timeoutId = null;
            }
          }
        });
      }, 3000);

      // Timeout after 2 minutes - clear stale state
      timeoutId = setTimeout(() => {
        if (!cancelled && pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
          setIdle();
          setError("Request timed out. Please try again.");
        }
      }, 120000);
    });

    return () => {
      cancelled = true;
      if (pollInterval) clearInterval(pollInterval);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [requestState.status, requestState.messageCount, activeSession, setIdle, refreshSessions, refreshCredits]);

  // Reset recovery flag when status changes away from "thinking"
  useEffect(() => {
    if (requestState.status !== "thinking") {
      recoveryStartedRef.current = false;
    }
  }, [requestState.status]);

  // Helper to recover state from server
  async function tryRecoverFromServer(
    podcastSlug: string,
    sessionId: string,
    expectedMessageCount: number,
  ): Promise<{ messages: AskSessionMessage[] } | null> {
    const session = await apiClient.getAskSession(podcastSlug, sessionId);

    // Check if server has more messages than when we started the request
    if (session.messages.length > expectedMessageCount) {
      return { messages: session.messages };
    }

    return null;
  }

  // Load session messages when active session changes
  const loadSessionMessages = useCallback(
    async (session: AskSession) => {
      setIsLoadingSession(true);
      setError("");
      setSessionExpired(false);

      try {
        const fullSession = await apiClient.getAskSession(
          session.podcast_slug,
          session.session_id,
        );
        setMessages(convertApiMessages(fullSession.messages));

        // Build episode reference counts from messages
        const counts: Record<number, number> = {};
        fullSession.messages.forEach((msg) => {
          if (msg.referenced_episode_numbers) {
            msg.referenced_episode_numbers.forEach((num) => {
              counts[num] = (counts[num] ?? 0) + 1;
            });
          }
        });
        setEpisodeReferenceCounts(counts);
      } catch (err) {
        console.error("Failed to load session messages", err);
        if (err instanceof SessionExpiredError) {
          setSessionExpired(true);
          setError("This session has expired. Please start a new session.");
        } else {
          setError("Failed to load session messages. Please try again.");
        }
      } finally {
        setIsLoadingSession(false);
      }
    },
    [],
  );

  // Handle session selection
  const handleSelectSession = useCallback(
    async (session: AskSession) => {
      // If selecting a session from a different podcast, switch context
      const podcast = availablePodcasts.find(
        (p) => p.id === session.podcast_slug,
      );
      if (podcast && podcast.id !== selectedPodcast?.id) {
        setSelectedPodcast(podcast);
      }

      // The useSessionRequestState hook will automatically load the pending state
      // from localStorage when activeSession changes, so isLoading will be correct

      setActiveSession(session);
      setIsDrawerOpen(false);
      await loadSessionMessages(session);
    },
    [availablePodcasts, selectedPodcast, setSelectedPodcast, loadSessionMessages],
  );

  // Handle new session creation
  const handleCreateSession = useCallback(() => {
    setIsNewSessionModalOpen(true);
  }, []);

  const handleConfirmNewSession = useCallback(
    async (podcastSlug: string) => {
      // Check if we need to show the warning
      if (!canCreateNew) {
        setPendingNewSessionSlug(podcastSlug);
        setIsNewSessionModalOpen(false);
        setIsDeleteWarningOpen(true);
        return;
      }

      await createSession(podcastSlug);
    },
    [canCreateNew],
  );

  const createSession = async (podcastSlug: string) => {
    setIsCreatingSession(true);

    try {
      const idempotencyKey = `create-session-${podcastSlug}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newSession = await apiClient.createAskSession(
        podcastSlug,
        idempotencyKey,
      );

      // Switch podcast context if needed
      const podcast = availablePodcasts.find((p) => p.id === podcastSlug);
      if (podcast && podcast.id !== selectedPodcast?.id) {
        setSelectedPodcast(podcast);
      }

      // New sessions don't have pending requests - the hook will auto-load idle state
      setActiveSession(newSession);
      setMessages([]);
      setEpisodeReferenceCounts({});
      setError("");
      setSessionExpired(false);
      refreshSessions();

      setIsNewSessionModalOpen(false);
      setIsDeleteWarningOpen(false);
      setIsDrawerOpen(false);
    } catch (err) {
      console.error("Failed to create session", err);
      setError("Failed to create session. Please try again.");
    } finally {
      setIsCreatingSession(false);
      setPendingNewSessionSlug(null);
    }
  };

  const handleConfirmDeleteWarning = useCallback(() => {
    if (pendingNewSessionSlug) {
      void createSession(pendingNewSessionSlug);
    }
  }, [pendingNewSessionSlug]);

  // Handle session deletion
  const handleDeleteSession = useCallback(
    async (session: AskSession) => {
      try {
        await apiClient.deleteAskSession(session.podcast_slug, session.session_id);

        // Clear pending state from localStorage
        clearForSession(session.session_id);

        refreshSessions();

        // If we deleted the active session, clear it
        if (activeSession?.session_id === session.session_id) {
          setActiveSession(null);
          setMessages([]);
          setEpisodeReferenceCounts({});
        }
      } catch (err) {
        console.error("Failed to delete session", err);
        setError("Failed to delete session. Please try again.");
      }
    },
    [activeSession, refreshSessions, clearForSession],
  );

  // Helper to check if an error is likely a network error (from backgrounding)
  function isNetworkError(err: unknown): boolean {
    if (err instanceof TypeError) {
      const message = err.message.toLowerCase();
      return (
        message.includes("failed to fetch") ||
        message.includes("network") ||
        message.includes("abort")
      );
    }
    if (err instanceof DOMException && err.name === "AbortError") {
      return true;
    }
    return false;
  }

  // Send message
  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    // Quick word count estimation (15,000 word limit)
    const wordCount = inputMessage.trim().split(/\s+/).filter(Boolean).length;
    if (wordCount > 15000) {
      setError(`Your message is too long (approximately ${wordCount.toLocaleString()} words). Please limit your question to 15,000 words or less.`);
      return;
    }

    if (!activeSession) {
      setError("Please select or create a session first.");
      return;
    }

    // Capture state for this request
    const requestSessionId = activeSession.session_id;
    const requestPodcastSlug = activeSession.podcast_slug;
    const question = inputMessage;
    const messageId = crypto.randomUUID();
    const currentMessageCount = messages.length;

    const userMessage: ChatMessage = {
      id: messageId,
      role: "user",
      content: question,
      timestamp: Date.now(),
    };

    // Update UI immediately
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setError("");

    // Save thinking state to localStorage (persists across session switches and app backgrounding)
    setThinking({
      question,
      messageId,
      messageCount: currentMessageCount + 1, // Include the user message we just added
    });

    // Reset backgrounded flag
    wasBackgroundedRef.current = false;

    try {
      const response = await apiClient.askInSession(
        requestPodcastSlug,
        requestSessionId,
        {
          question,
          enable_thinking: enableThinking,
          k: 12,
          max_context_tokens: 20000,
          score_threshold: 0.4,
        },
      );

      // Clear thinking state - request completed successfully
      setIdle();

      // If user switched sessions while waiting, don't update UI
      // (the response is already saved on server, user will see it when they switch back)
      if (activeSession?.session_id !== requestSessionId) {
        return;
      }

      const uniqueReferencedNumbers = extractReferencedEpisodeNumbers(
        response.referenced_episode_numbers,
        response.metadata,
      );

      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: response.answer,
        timestamp: Date.now(),
        responseId: response.response_id,
        verdict: response.verdict === "no_context" ? "no_context" : "answer",
        usedContext: response.used_context,
        chunksUsed: response.chunks_used,
        model: response.model,
        referencedEpisodeNumbers: uniqueReferencedNumbers,
        metadata: response.metadata ?? null,
      };

      if (uniqueReferencedNumbers.length) {
        setEpisodeReferenceCounts((prevCounts) => {
          const nextCounts = { ...prevCounts };
          uniqueReferencedNumbers.forEach((value) => {
            nextCounts[value] = (nextCounts[value] ?? 0) + 1;
          });
          return nextCounts;
        });
      }

      setMessages((prev) => [...prev, assistantMessage]);

      // Refresh sessions to update message count and snippet
      refreshSessions();

      // Refresh credits since /ask consumes credits
      void refreshCredits();
    } catch (err) {
      // If user switched sessions, clear state but don't show error
      if (activeSession?.session_id !== requestSessionId) {
        setIdle();
        return;
      }

      console.error("Error asking question", err);

      // If app was backgrounded or this looks like a network error,
      // try to recover from server before showing error
      if (wasBackgroundedRef.current || isNetworkError(err)) {
        try {
          const recovered = await tryRecoverFromServer(
            requestPodcastSlug,
            requestSessionId,
            currentMessageCount + 1, // Expected count after user message
          );

          if (recovered) {
            // Server has the answer - update state
            setMessages(convertApiMessages(recovered.messages));

            // Rebuild episode reference counts
            const counts: Record<number, number> = {};
            recovered.messages.forEach((msg) => {
              if (msg.referenced_episode_numbers) {
                msg.referenced_episode_numbers.forEach((num) => {
                  counts[num] = (counts[num] ?? 0) + 1;
                });
              }
            });
            setEpisodeReferenceCounts(counts);

            // Clear pending state
            setIdle();
            setError("");
            wasBackgroundedRef.current = false;

            // Refresh sessions list and credits
            refreshSessions();
            void refreshCredits();
            return;
          }
        } catch (recoveryErr) {
          // Recovery failed - fall through to show original error
          console.error("Recovery attempt failed", recoveryErr);

          if (recoveryErr instanceof SessionExpiredError) {
            setIdle();
            setSessionExpired(true);
            setError("This session has expired. Please start a new session.");
            return;
          }
        }
      }

      // Handle the error
      if (err instanceof SessionExpiredError) {
        setIdle();
        setSessionExpired(true);
        setError("This session has expired. Please start a new session.");
      } else {
        const details = err instanceof Error ? err.message : "Unknown error";

        // Store error state for potential retry
        setRequestError({
          error: details,
          question,
          messageId,
        });

        setError(`An error occurred: ${details}`);

        const errorMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: `I'm sorry, I encountered an error: ${details}. Please try again.`,
          timestamp: Date.now(),
        };

        setMessages((prev) => [...prev, errorMessage]);
      }
    }
  };

  const clearChat = () => {
    setMessages([]);
    setEpisodeReferenceCounts({});
    setSelectedEpisode(null);
  };

  const episodesByNumber = useMemo(() => {
    const map = new Map<number, Episode>();
    episodes.forEach((episode) => {
      if (
        typeof episode.episodeNumber === "number" &&
        Number.isFinite(episode.episodeNumber)
      ) {
        map.set(episode.episodeNumber, episode);
      }
    });
    return map;
  }, [episodes]);

  const referencedEpisodes = useMemo(() => {
    const entries = Object.entries(episodeReferenceCounts);
    if (entries.length === 0) {
      return [];
    }

    const collected: Array<{
      episode: Episode;
      count: number;
      publishedAt: number;
    }> = [];
    entries.forEach(([rawKey, count]) => {
      const episodeNumber = Number(rawKey);
      if (!Number.isFinite(episodeNumber)) {
        return;
      }
      const episode = episodesByNumber.get(episodeNumber);
      if (!episode) {
        return;
      }
      const publishedAt = new Date(episode.publishedDate ?? 0).getTime();
      collected.push({
        episode,
        count,
        publishedAt: Number.isFinite(publishedAt) ? publishedAt : 0,
      });
    });

    collected.sort((left, right) => {
      if (right.count !== left.count) {
        return right.count - left.count;
      }
      if (right.publishedAt !== left.publishedAt) {
        return right.publishedAt - left.publishedAt;
      }
      return left.episode.title.localeCompare(right.episode.title);
    });

    return collected;
  }, [episodeReferenceCounts, episodesByNumber]);

  const referencedEpisodeNumbers = useMemo(
    () =>
      Object.keys(episodeReferenceCounts)
        .map((episodeNumber) => Number(episodeNumber))
        .filter((episodeNumber) => Number.isFinite(episodeNumber)),
    [episodeReferenceCounts],
  );

  const chatEvidenceMetadata = useMemo(
    () =>
      messages.flatMap((message) =>
        message.role === "assistant" && message.metadata ? [message.metadata] : [],
      ),
    [messages],
  );

  const markdownComponents = useMemo(() => {
    const baseClass = "text-gray-900 dark:text-gray-100";
    return {
      p: ({ children, ...props }: any) => (
        <p className={`mb-4 leading-relaxed ${baseClass}`} {...props}>
          {children}
        </p>
      ),
      h1: ({ children, ...props }: any) => (
        <h1
          className={`mt-6 mb-4 text-2xl font-semibold ${baseClass}`}
          {...props}
        >
          {children}
        </h1>
      ),
      h2: ({ children, ...props }: any) => (
        <h2
          className={`mt-5 mb-3 text-xl font-semibold ${baseClass}`}
          {...props}
        >
          {children}
        </h2>
      ),
      h3: ({ children, ...props }: any) => (
        <h3
          className={`mt-4 mb-2 text-lg font-semibold ${baseClass}`}
          {...props}
        >
          {children}
        </h3>
      ),
      a: ({ children, ...props }: any) => (
        <a
          className="font-semibold text-gray-700 underline decoration-2 underline-offset-4 hover:text-gray-900 dark:text-amber-400 dark:hover:text-amber-200"
          target="_blank"
          rel="noreferrer"
          {...props}
        >
          {children}
        </a>
      ),
      ul: ({ children, ...props }: any) => (
        <ul
          className={`mb-4 list-disc space-y-2 pl-6 ${baseClass}`}
          {...props}
        >
          {children}
        </ul>
      ),
      ol: ({ children, ...props }: any) => (
        <ol
          className={`mb-4 list-decimal space-y-2 pl-6 ${baseClass}`}
          {...props}
        >
          {children}
        </ol>
      ),
      li: ({ children, ...props }: any) => (
        <li className="leading-relaxed" {...props}>
          {children}
        </li>
      ),
      blockquote: ({ children, ...props }: any) => (
        <blockquote
          className="my-4 border-l-4 border-gray-300 bg-gray-100/60 pl-4 italic text-gray-700 dark:border-gray-600 dark:bg-gray-800/60 dark:text-gray-200"
          {...props}
        >
          {children}
        </blockquote>
      ),
      code: ({ inline, className, children, ...props }: any) => {
        const codeContent = Array.isArray(children)
          ? children.join("")
          : String(children ?? "");
        if (inline) {
          return (
            <code
              className="rounded-md bg-gray-200 px-1.5 py-0.5 font-mono text-sm text-gray-800 dark:bg-gray-800 dark:text-gray-100"
              {...props}
            >
              {codeContent}
            </code>
          );
        }
        return (
          <pre className="my-5 overflow-x-auto rounded-xl border border-gray-200 bg-gray-900 p-4 text-sm text-gray-100 dark:border-gray-700">
            <code className={`font-mono ${className ?? ""}`} {...props}>
              {codeContent.replace(/\n$/, "")}
            </code>
          </pre>
        );
      },
      table: ({ children, ...props }: any) => (
        <div className="my-4 overflow-x-auto">
          <table
            className={`w-full border-collapse text-left text-sm ${baseClass}`}
            {...props}
          >
            {children}
          </table>
        </div>
      ),
      thead: ({ children, ...props }: any) => (
        <thead className="bg-gray-100 dark:bg-gray-800" {...props}>
          {children}
        </thead>
      ),
      th: ({ children, ...props }: any) => (
        <th
          className="border-b border-gray-200 px-3 py-2 font-semibold text-gray-900 dark:border-gray-700 dark:text-gray-100"
          {...props}
        >
          {children}
        </th>
      ),
      td: ({ children, ...props }: any) => (
        <td
          className="border-b border-gray-200 px-3 py-2 align-top text-gray-700 dark:border-gray-700 dark:text-gray-200"
          {...props}
        >
          {children}
        </td>
      ),
      hr: (props: any) => (
        <hr
          className="my-6 border-dashed border-gray-300 dark:border-gray-700"
          {...props}
        />
      ),
    };
  }, []);

  const noPodcasts = !podcastLoading && availablePodcasts.length === 0;

  // Find the podcast for the oldest session (for delete warning)
  const oldestSessionPodcast = oldestSession
    ? availablePodcasts.find((p) => p.id === oldestSession.podcast_slug) ?? null
    : null;

  if (podcastLoading) {
    return (
      <div
        className={cn(
          "min-h-screen",
          theme.colors.backgroundCream,
          "flex flex-col",
        )}
      >
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="flex flex-col items-center gap-3 text-gray-700 dark:text-gray-200">
            <div
              className={cn(
                "w-12 h-12 border-4 border-gray-200 dark:border-gray-700 border-t-transparent rounded-full animate-spin",
              )}
            />
            <p className="text-lg font-medium">Loading podcasts…</p>
          </div>
        </main>
      </div>
    );
  }

  if (noPodcasts) {
    return (
      <div
        className={cn("min-h-screen flex flex-col", theme.colors.backgroundCream)}
      >
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div
            className={cn(
              "max-w-2xl w-full text-center space-y-6 px-8 py-10",
              theme.cards.base,
            )}
          >
            <div
              className={cn(
                "mx-auto w-16 h-16 flex items-center justify-center rounded-full",
                theme.colors.primaryOrange,
                theme.borders.neubrutalism,
                theme.shadows.neubrutalismSm,
              )}
            >
              <span className="text-3xl">💬</span>
            </div>
            <div className="space-y-3">
              <h2 className="text-3xl font-black text-gray-900 dark:text-white">
                Add a podcast to start chatting
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300">
                You need at least one podcast connected before you can ask
                questions. Add your first podcast to begin.
              </p>
            </div>
            <div className="flex justify-center">
              <Link
                to="/podcasts/add"
                className={cn(theme.buttons.primary, "inline-flex px-6 py-3")}
              >
                Add a podcast
              </Link>
            </div>
            <div className="flex items-center gap-4 text-gray-500 dark:text-gray-400">
              <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
              <span className="text-sm font-medium">OR</span>
              <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
            </div>
            <p className="text-gray-700 dark:text-gray-300">
              <Link
                to="/try"
                className={cn("font-semibold hover:underline", theme.text.primaryOrange)}
              >
                Chat with up to 5 podcast episodes
              </Link>{" "}
              without subscribing.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${theme.colors.backgroundCream} transition-colors duration-200 flex flex-col`}
    >
      <Header />

      <main className="flex-1 flex">
        {/* Desktop Sidebar */}
        <aside className="hidden md:flex w-72 flex-shrink-0 overflow-hidden border-r border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-800/50">
          <SessionSidebar
            sessions={globalSessions}
            podcasts={availablePodcasts}
            activeSessionId={activeSession?.session_id ?? null}
            onSelectSession={handleSelectSession}
            onCreateSession={handleCreateSession}
            onDeleteSession={handleDeleteSession}
            isLoading={sessionsLoading}
          />
        </aside>

        {/* Mobile Drawer */}
        <MobileSessionDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
        >
          <SessionSidebar
            sessions={globalSessions}
            podcasts={availablePodcasts}
            activeSessionId={activeSession?.session_id ?? null}
            onSelectSession={handleSelectSession}
            onCreateSession={handleCreateSession}
            onDeleteSession={handleDeleteSession}
            isLoading={sessionsLoading}
          />
        </MobileSessionDrawer>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-4xl mx-auto px-4 py-6">
              {/* Mobile header with drawer toggle */}
              <div className="md:hidden flex items-center gap-3 mb-4">
                <button
                  onClick={() => setIsDrawerOpen(true)}
                  className={cn(
                    "p-2 rounded-lg",
                    "bg-white dark:bg-gray-800",
                    "border-2 border-gray-300 dark:border-gray-600",
                    "hover:border-gray-400 dark:hover:border-gray-500",
                  )}
                  aria-label="Open sessions"
                >
                  <Bars3Icon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                </button>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {globalSessions.length}/3 sessions
                </span>
              </div>

              {/* Session header */}
              {activeSession && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <span>Chat with</span>
                    {displayedPodcast && (
                      <span
                        className={cn(
                          "inline-flex items-center gap-2 px-3 py-1 rounded-lg text-base sm:text-lg",
                          theme.colors.primaryOrange,
                          theme.borders.neubrutalism,
                          theme.shadows.neubrutalismSm,
                        )}
                      >
                        <span className="text-xl">{displayedPodcast.emoji}</span>
                        <span>{displayedPodcast.name}</span>
                      </span>
                    )}
                  </h2>

                  <div className="flex items-center gap-3">
                    {/* Timer */}
                    <div
                      className={cn(
                        "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium",
                        expiringSoon
                          ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400",
                      )}
                    >
                      <ClockIcon className="w-4 h-4" />
                      <span>{timeRemaining}</span>
                    </div>

                    {messages.length > 0 && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            const formatted = messages
                              .map((msg) => {
                                const sender =
                                  msg.role === "user" ? "You" : "AI Assistant";
                                const time = new Date(
                                  msg.timestamp,
                                ).toLocaleTimeString();
                                const body = formatMessageForCopy(msg);
                                return `## ${sender} (${time})\n\n${body}\n\n`;
                              })
                              .join("---\n\n");
                            void navigator.clipboard.writeText(formatted);
                            setCopiedChat(true);
                            setTimeout(() => setCopiedChat(false), 2000);
                          }}
                          className={cn(
                            "px-3 py-1.5 rounded-lg text-sm font-medium",
                            "bg-white dark:bg-gray-800",
                            "border-2 border-gray-300 dark:border-gray-600",
                            "hover:border-gray-400 dark:hover:border-gray-500",
                            "flex items-center gap-1.5",
                          )}
                        >
                          {copiedChat ? (
                            <>
                              <CheckIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                              Copied
                            </>
                          ) : (
                            <>
                              <ClipboardDocumentIcon className="h-4 w-4" />
                              Copy
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Podcast indexing banner */}
              {isCurrentPodcastProcessing && activeSession && (
                <div
                  className={cn(
                    "p-6 rounded-xl mb-6 text-center",
                    "bg-amber-50 dark:bg-amber-900/20",
                    "border-3 border-amber-500 dark:border-amber-600",
                  )}
                >
                  <h3 className="text-lg font-bold text-amber-800 dark:text-amber-300 mb-2">
                    Podcast Indexing
                  </h3>
                  <p className="text-amber-700 dark:text-amber-400">
                    We're indexing your podcast. This can take up to 24 hours.
                    You'll receive an email when it's ready.
                  </p>
                </div>
              )}

              {/* No session selected state */}
              {!activeSession && !sessionsLoading && (
                <div className="flex flex-col items-center justify-center py-16">
                  <div
                    className={cn(
                      "w-20 h-20 flex items-center justify-center rounded-full mb-6",
                      theme.colors.primaryOrange,
                      theme.borders.neubrutalism,
                      theme.shadows.neubrutalismSm,
                    )}
                  >
                    <span className="text-4xl">💬</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Start a Conversation
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 text-center mb-6 max-w-md">
                    {globalSessions.length > 0
                      ? "Select a session from the sidebar or start a new one to begin chatting."
                      : "Create your first session to start asking questions about your podcasts."}
                  </p>
                  <button
                    onClick={handleCreateSession}
                    className={cn(theme.buttons.primary, "px-6 py-2.5 rounded-lg")}
                  >
                    New Session
                  </button>
                </div>
              )}

              {/* Session expired state */}
              {sessionExpired && (
                <div
                  className={cn(
                    "p-6 rounded-xl mb-6 text-center",
                    "bg-amber-50 dark:bg-amber-900/20",
                    "border-3 border-amber-500 dark:border-amber-600",
                  )}
                >
                  <h3 className="text-lg font-bold text-amber-800 dark:text-amber-300 mb-2">
                    Session Expired
                  </h3>
                  <p className="text-amber-700 dark:text-amber-400 mb-4">
                    This conversation has expired after 4 hours. Your message
                    history is no longer available.
                  </p>
                  <button
                    onClick={() => {
                      setActiveSession(null);
                      setSessionExpired(false);
                      setMessages([]);
                      handleCreateSession();
                    }}
                    className={cn(theme.buttons.primary, "px-4 py-2 rounded-lg")}
                  >
                    Start New Session
                  </button>
                </div>
              )}

              {error && !sessionExpired && (
                <div className="bg-red-100 dark:bg-red-900 border-3 border-red-500 rounded-xl p-4 mb-6">
                  <p className="text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}

              {/* Chat container */}
              {activeSession && !sessionExpired && (
                <>
                  <div className="relative mb-6">
                    <div
                      className={`absolute inset-0 ${theme.colors.slate950} rounded-xl translate-y-2 translate-x-2`}
                    />
                    <div
                      ref={chatContainerRef}
                      className={`relative z-10 ${theme.cards.episode} p-4 min-h-[400px] max-h-[500px] md:max-h-[600px] lg:max-h-[65vh] overflow-y-auto`}
                    >
                      {isLoadingSession ? (
                        <div className="h-full flex items-center justify-center">
                          <div className="flex flex-col items-center gap-3">
                            <div className="w-8 h-8 border-3 border-gray-300 dark:border-gray-600 border-t-primary-orange dark:border-t-primary-orange-dark rounded-full animate-spin" />
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Loading messages...
                            </p>
                          </div>
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                          <p className="text-center mb-2">
                            Start chatting about this podcast!
                          </p>
                          <p className="text-center text-sm">
                            Ask questions about episodes, topics, or themes.
                          </p>
                          {displayedPodcast && (
                            <p className="text-center text-sm mt-2">
                              Chatting about:{" "}
                              <strong>{displayedPodcast.name}</strong>{" "}
                              {displayedPodcast.emoji}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {messages.map((message, index) => (
                            <div
                              key={message.id ?? `msg-${index}`}
                              className={
                                message.role === "user"
                                  ? "ml-auto max-w-[85%]"
                                  : "mr-auto max-w-[85%]"
                              }
                            >
                              <div
                                className={`relative p-4 rounded-xl ${
                                  message.role === "user"
                                    ? "bg-blue-100 dark:bg-blue-900 border-3 border-blue-500 dark:border-blue-700"
                                    : "bg-gray-100 dark:bg-gray-700 border-3 border-gray-900 dark:border-gray-600"
                                }`}
                              >
                                <div className="flex justify-between items-start mb-2 gap-3">
                                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {message.role === "user"
                                      ? "You"
                                      : "AI Assistant"}
                                  </div>
                                  <button
                                    onClick={() => {
                                      void navigator.clipboard.writeText(
                                        formatMessageForCopy(message),
                                      );
                                      setCopiedMessageId(message.id);
                                      setTimeout(
                                        () => setCopiedMessageId(null),
                                        2000,
                                      );
                                    }}
                                    className="text-xs text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white transition-colors"
                                  >
                                    {copiedMessageId === message.id
                                      ? "✓ Copied"
                                      : "Copy"}
                                  </button>
                                </div>

                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  components={markdownComponents as any}
                                >
                                  {message.content}
                                </ReactMarkdown>

                                <div className="mt-3 text-xs text-gray-500 dark:text-gray-300">
                                  <span>
                                    {new Date(
                                      message.timestamp,
                                    ).toLocaleTimeString()}
                                  </span>
                                  {message.verdict && (
                                    <span className="ml-2 font-semibold">
                                      {message.verdict === "answer"
                                        ? "Answer provided"
                                        : message.verdict === "no_context"
                                          ? "No context"
                                          : "Insufficient context"}
                                    </span>
                                  )}
                                  {message.usedContext && (
                                    <span className="ml-2">
                                      {formatUsedContext(message.usedContext)}
                                    </span>
                                  )}
                                  {typeof message.chunksUsed === "number" && (
                                    <span className="ml-2">
                                      Chunks: {message.chunksUsed}
                                    </span>
                                  )}
                                  {message.model && (
                                    <span className="ml-2">
                                      Model: {message.model}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                          {isLoading && (
                            <div className="mr-auto max-w-[85%]">
                              <div
                                className={`relative p-4 rounded-xl bg-gray-100 dark:bg-gray-700 ${theme.borders.neubrutalism}`}
                              >
                                <div className="flex space-x-2">
                                  <span
                                    className="h-2.5 w-2.5 rounded-full bg-gray-500 dark:bg-gray-300 animate-[chat-bounce_1.2s_infinite]"
                                    style={{ animationDelay: "0ms" }}
                                  />
                                  <span
                                    className="h-2.5 w-2.5 rounded-full bg-gray-500 dark:bg-gray-300 animate-[chat-bounce_1.2s_infinite]"
                                    style={{ animationDelay: "150ms" }}
                                  />
                                  <span
                                    className="h-2.5 w-2.5 rounded-full bg-gray-500 dark:bg-gray-300 animate-[chat-bounce_1.2s_infinite]"
                                    style={{ animationDelay: "300ms" }}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  </div>

                  {/* Input area */}
                  <div className="relative">
                    <div
                      className={`absolute inset-0 ${theme.colors.slate950} rounded-xl translate-y-2 translate-x-2`}
                    />
                    <div
                      className={`relative z-10 bg-white dark:bg-gray-800 ${theme.borders.slate950} rounded-xl p-4`}
                    >
                      <textarea
                        value={inputMessage}
                        onChange={(event) =>
                          setInputMessage(event.target.value)
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter" && !event.shiftKey) {
                            event.preventDefault();
                            void sendMessage();
                          }
                        }}
                        placeholder="Ask about episodes, topics, or people..."
                        className={`w-full min-h-[100px] bg-transparent border-2 border-gray-200 dark:border-gray-600 rounded-xl p-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 ${theme.ring.primaryOrange}`}
                        disabled={isLoading || isLoadingSession || isCurrentPodcastProcessing}
                      />
                      <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="text-sm text-gray-500 dark:text-gray-300">
                          Shift+Enter for newline
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setEnableThinking(!enableThinking)}
                            disabled={isLoading || isLoadingSession || isCurrentPodcastProcessing}
                            className={cn(
                              "px-3 py-2 text-sm font-medium rounded-xl transition-all",
                              "disabled:opacity-50 disabled:cursor-not-allowed",
                              enableThinking
                                ? theme.buttons.primary
                                : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-3 border-gray-900 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                            )}
                          >
                            🧠 Deep thinking {enableThinking ? "(ON)" : "(OFF)"}
                          </button>
                          <button
                            onClick={() => void sendMessage()}
                            disabled={
                              isLoading ||
                              isLoadingSession ||
                              isCurrentPodcastProcessing ||
                              !inputMessage.trim()
                            }
                            className={`inline-flex items-center justify-center px-5 py-2 rounded-xl ${theme.buttons.primary} focus:outline-none focus:ring-4 focus:ring-primary-orange/40 dark:focus:ring-primary-orange-dark/30 disabled:cursor-not-allowed disabled:opacity-75`}
                          >
                            {isLoading ? "Thinking…" : "Send"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Referenced episodes */}
                  {Object.keys(episodeReferenceCounts).length > 0 && (
                    <section className="mt-8">
                      <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Episodes referenced in this chat
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Sorted by reference count and publication date.
                        </p>
                      </div>

                      {chatEvidenceMetadata.length > 0 && (
                        <AnswerEvidencePanel
                          metadataEntries={chatEvidenceMetadata}
                          fallbackEpisodeNumbers={referencedEpisodeNumbers}
                          episodesByNumber={episodesByNumber}
                          onOpenEpisode={setSelectedEpisode}
                        />
                      )}

                      {episodesLoading ? (
                        <div className="rounded-xl border-3 border-dashed border-gray-300 dark:border-gray-600 px-4 py-6 text-center text-sm text-gray-600 dark:text-gray-300">
                          Loading referenced episodes…
                        </div>
                      ) : referencedEpisodes.length === 0 ? (
                        <div className="rounded-xl border-3 border-dashed border-gray-300 dark:border-gray-600 px-4 py-6 text-center text-sm text-gray-600 dark:text-gray-300">
                          We couldn't match the referenced episodes yet. Ask
                          another question to refresh the list.
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                          {referencedEpisodes.map(({ episode, count }, index) => (
                            <div key={episode.guid ?? `episode-${index}`} className="flex flex-col">
                              <EpisodeCard
                                episode={episode}
                                onClick={() => setSelectedEpisode(episode)}
                                compact
                              />
                              <p className="mt-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">
                                Referenced {count}{" "}
                                {count === 1 ? "time" : "times"}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  )}
                </>
              )}

              {selectedEpisode && (
                <EpisodeModal
                  episode={selectedEpisode}
                  onClose={() => setSelectedEpisode(null)}
                />
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      <NewSessionModal
        isOpen={isNewSessionModalOpen}
        onClose={() => setIsNewSessionModalOpen(false)}
        onConfirm={handleConfirmNewSession}
        podcasts={availablePodcasts}
        currentPodcast={selectedPodcast}
        isCreating={isCreatingSession}
        processingPodcastIds={processingPodcastIds}
      />

      <DeleteSessionWarningModal
        isOpen={isDeleteWarningOpen}
        onClose={() => {
          setIsDeleteWarningOpen(false);
          setPendingNewSessionSlug(null);
        }}
        onConfirm={handleConfirmDeleteWarning}
        sessionToDelete={oldestSession}
        podcast={oldestSessionPodcast}
        isCreating={isCreatingSession}
      />

      <Footer />
    </div>
  );
}
